import { AccessorFn, CellContext, ColumnDef } from '@tanstack/react-table';
import _ from 'lodash';

import {
  finleyAssert,
  error as commonError,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import {
  AssetVerification,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import {
  AssetVerificationReceivableContainer,
  AssetVerificationReceivable,
  AssetVerificationSummaryCounts,
  BaseFieldConfigMap,
  LenderVerificationStatus,
  AssetVerificationStatusReceivables,
} from './types';

export const getVerificationField = (
  baseFieldKey: keyof AssetVerificationReceivableContainer['baseFieldConfigsMap'],
  fields: AssetVerificationReceivable['fields'],
) => {
  const verificationField = _.find(
    fields,
    ({ field }) => field === baseFieldKey,
  );
  finleyAssert(verificationField, 'Expected verified property to exist');

  return verificationField;
};

export const getVerificationFieldResult = (
  assetVerificationReceivableField: AssetVerificationReceivable['fields'][number],
) => {
  if (assetVerificationReceivableField.result === null) {
    return null;
  }
  if (assetVerificationReceivableField.result) {
    return assetVerificationReceivableField.result.verificationResult;
  }
  throw new commonError.FinleyError(
    'Expected AssetVerificationField to be either Passed, Failed, or Untested',
    {
      metadata: {
        logMeta: {
          assetVerificationReceivableField,
        },
      },
    },
  );
};

export function createTanstackColumnConfig<TData = any, TCell = any>({
  baseFieldKey,
  accessorFn,
  cell,
  enableSorting,
  enableColumnFilter,
}: {
  baseFieldKey: string;
  accessorFn?: AccessorFn<TData, TCell>;
  cell: (input: CellContext<TData, TCell>) => any;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
}): ColumnDef<TData, any> {
  return {
    id: baseFieldKey,
    header: _.startCase(baseFieldKey),
    accessorFn,
    cell,
    enableSorting,
    enableColumnFilter,
  };
}

// ==================== Transformation Utils ====================

const getVerificationStatus = (
  receivable: AssetVerification.AssetVerificationOutput.Receivable,
): LenderVerificationStatus => {
  switch (receivable.verificationStatus) {
    case 'Untested':
      return 'untested';
    case 'VerifiedFailure':
      return 'failed';
    case 'Passed':
      return 'passed';
    case 'TextractFailed':
    case 'UnverifiedFailure':
      return 'finleyReview';
    default:
      return finleyAssertUnreachable(receivable.verificationStatus);
  }
};

export const transformAssetVerificationReportToAssetVerificationReceivableContainer = (
  input: string,
): AssetVerificationReceivableContainer => {
  try {
    const assetVerificationReport = JSON.parse(input);

    finleyAssert(
      finleyTypeguards.assetVerification.isAssetVerificationReport(
        assetVerificationReport,
      ),
      'Expected asset verification report to be valid',
    );

    const baseFieldConfigsMap: BaseFieldConfigMap = _.keyBy(
      assetVerificationReport.baseFieldConfigs,
      ({ field }) => field,
    );

    const receivables: AssetVerificationReceivable[] = assetVerificationReport.receivables.map(
      (receivable: AssetVerification.AssetVerificationOutput.Receivable) => {
        const rawIdsWithTimestamps = receivable.documentations.map(
          ({ rawId, timestamp }) => ({
            id: rawId,
            timestamp,
          }),
        );

        // extract information about latest documentation (if found)
        return {
          ...receivable,
          rawIdsWithTimestamps,
          lenderVerificationStatus: getVerificationStatus(receivable),
        };
      },
    );

    const groupedReceivables = _.groupBy(
      receivables,
      ({ lenderVerificationStatus }) => lenderVerificationStatus,
    );

    const statusReceivables: AssetVerificationStatusReceivables = {
      untested: groupedReceivables.untested ?? [],
      failed: groupedReceivables.failed ?? [],
      finleyReview: groupedReceivables.finleyReview ?? [],
      passed: groupedReceivables.passed ?? [],
    };

    const summaryCounts: AssetVerificationSummaryCounts = {
      untested: statusReceivables.untested.length,
      failed: statusReceivables.failed.length,
      finleyReview: statusReceivables.finleyReview.length,
      passed: statusReceivables.passed.length,
    };

    return {
      baseFieldConfigsMap,
      summaryCounts,
      statusReceivables,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    throw new commonError.FinleyError(
      'Failed to transform asset verification report receivables',
      { metadata: { logMeta: { input, err } } },
    );
  }
};
